import { useState } from 'react';
import { useAvailableUpgrades, useCreateAction, useAction } from '../../hooks/useActions';

interface UpgradePanelProps {
  subscriptionId: string;
  currentVersion: string;
}

export default function UpgradePanel({ subscriptionId, currentVersion }: UpgradePanelProps) {
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const { data: upgradeData, isLoading: loadingUpgrades } = useAvailableUpgrades(subscriptionId);
  const { data: activeAction } = useAction(activeActionId);
  const createAction = useCreateAction();

  const handleUpgrade = async () => {
    if (!selectedVersion) return;

    try {
      const action = await createAction.mutateAsync({
        subscription_id: subscriptionId,
        action_type: 'update',
        payload: { version: selectedVersion },
      });
      setActiveActionId(action.id);
    } catch (error) {
      console.error('Failed to create upgrade action:', error);
    }
  };

  const handleDismiss = () => {
    setActiveActionId(null);
    setSelectedVersion('');
  };

  // Show active action status
  if (activeAction) {
    const payload = activeAction.action_payload ? JSON.parse(activeAction.action_payload) : {};

    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {activeAction.status === 'pending' || activeAction.status === 'claimed' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="font-medium">Waiting for server...</p>
              <p className="text-sm text-gray-500">
                Upgrade to {payload.version} queued
              </p>
            </div>
          </div>
        ) : activeAction.status === 'running' ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="font-medium">Upgrading...</p>
              <p className="text-sm text-gray-500">{activeAction.result || 'In progress'}</p>
            </div>
          </div>
        ) : activeAction.status === 'completed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-700">Upgrade Complete</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : activeAction.status === 'failed' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-red-700">Upgrade Failed</p>
                <p className="text-sm text-gray-500">{activeAction.result}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // No upgrades available
  if (!loadingUpgrades && (!upgradeData?.available_upgrades || upgradeData.available_upgrades.length === 0)) {
    return (
      <div className="text-sm text-gray-500">
        Running latest version ({currentVersion})
      </div>
    );
  }

  // Show upgrade selector
  return (
    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
      <div className="flex items-center gap-2 mb-3">
        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span className="font-medium text-blue-700">Updates Available</span>
      </div>

      {loadingUpgrades ? (
        <div className="text-sm text-gray-500">Loading versions...</div>
      ) : (
        <div className="flex items-center gap-3">
          <select
            value={selectedVersion}
            onChange={(e) => setSelectedVersion(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select version...</option>
            {upgradeData?.available_upgrades.map((upgrade, index) => (
              <option key={upgrade.version} value={upgrade.version}>
                {upgrade.version} {index === 0 ? '(Latest)' : ''}
              </option>
            ))}
          </select>

          <button
            onClick={handleUpgrade}
            disabled={!selectedVersion || createAction.isPending}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createAction.isPending ? 'Starting...' : 'Upgrade'}
          </button>
        </div>
      )}

      {createAction.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(createAction.error as Error)?.message || 'Failed to start upgrade'}
        </p>
      )}
    </div>
  );
}
